import { useCallback } from 'react';
import { useGetMeQuery } from '../store/redux/api/accountApi';
import { useGetCurrentSubscriptionQuery } from '../store/redux/api/billingApi';

interface Entitlement {
  key: string;
  currentValue?: number;
  limitValue?: number;
}

export function useTemplateLimitReached() {
  const { data: me } = useGetMeQuery(undefined);
  const billingEnabled = Boolean(me?.billing_enabled);
  const entitlementsActive = Boolean(me?.entitlements_active);
  const shouldCheck = billingEnabled || entitlementsActive;

  const { data, isLoading, refetch } = useGetCurrentSubscriptionQuery(
    undefined,
    { skip: !shouldCheck }
  );

  // Publishing a template changes these counts server-side, but the billing
  // query has no cache tags for `templateApi` to invalidate — it lives in a
  // separate API slice. Callers that display a count refresh it themselves.
  const refresh = useCallback(() => {
    if (shouldCheck) refetch();
  }, [shouldCheck, refetch]);

  if (!shouldCheck) {
    return {
      isLimitReached: false,
      isLoading: false,
      currentValue: 0,
      limitValue: Infinity,
      billingEnabled,
      refresh,
    };
  }

  const entitlements: Entitlement[] =
    data?.subscription?.entitlements ||
    data?.organisation?.entitlements ||
    data?.trial?.entitlements ||
    [];

  const entitlement = entitlements.find((e) => e.key === 'pass_templates');

  const currentValue = entitlement?.currentValue ?? 0;
  const limitValue = entitlement?.limitValue ?? Infinity;
  const isLimitReached = currentValue >= limitValue;

  return {
    isLimitReached,
    isLoading,
    currentValue,
    limitValue,
    billingEnabled,
    refresh,
  };
}
